import { FC } from 'react';
import { FloatingTooltip } from 'components/floating-tooltip';
import { format0 } from 'utils/number';
import type { AddressRunSummary, Run } from '../types';
import styles from '../page.module.scss';

// The candidates heading: the run-wide group count, the (truncatable) scope
// line, and the match summary. The count sums the run-wide per-status counts,
// not the filtered row total, so a search can't shrink it.
export const CandidatesHeading: FC<{
  candidatesTotal: number;
  scopeInfo: { summary: string; full: string } | null;
  summary: AddressRunSummary | null;
  executions: Run[];
}> = ({ candidatesTotal, scopeInfo, summary, executions }) => {
  // Before any execution the trailing figure is the pre-run preview: the
  // run-wide `documents_moving` sum, which falls to 0 once every group is
  // combined. After an execution show what actually moved — the executions'
  // "Citations moved" total (citation rows, the same unit and label the
  // Executions table uses) — rather than leaving the now-zero preview on screen.
  const executed = executions.length > 0;
  const referencesMoved = executions.reduce(
    (sum, run) => sum + (run.documentReferencesMoved ?? 0),
    0
  );
  return (
    <div className={styles.candidatesHeading}>
      <h2 className={styles.candidatesHeadingTitle}>
        Candidates ({format0(candidatesTotal)} address groups)
      </h2>
      {scopeInfo &&
        (scopeInfo.full === scopeInfo.summary ? (
          <span
            className={styles.candidatesHeadingMeta}
          >{`Scope: ${scopeInfo.summary}`}</span>
        ) : (
          // The full area list lives in a focusable, keyboard/touch-reachable
          // tooltip (not a mouse-only `title`), with the truncated summary
          // staying visible.
          <FloatingTooltip content={scopeInfo.full} placement="bottom">
            <span
              className={styles.candidatesHeadingMeta}
            >{`Scope: ${scopeInfo.summary}`}</span>
          </FloatingTooltip>
        ))}
      {summary && (
        <span className={styles.candidatesHeadingMeta}>
          {`| Matched on — Identical text: ${format0(
            summary.identicalGroups
          )} · Standardized: ${format0(
            summary.standardizedGroups
          )} · Addresses in scope: ${format0(
            summary.addressesBefore
          )} → ${format0(summary.addressesAfter)} · ${
            executed
              ? `Citations moved: ${format0(referencesMoved)}`
              : `Documents moving: ${format0(summary.documentReferencesMoving)}`
          }`}
        </span>
      )}
    </div>
  );
};
